import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';

const Shape = ({ position, geometry, color, speed, rotationAxis }) => {
  const meshRef = useRef();

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime * speed;
    meshRef.current.rotation.x = rotationAxis[0] * t;
    meshRef.current.rotation.y = rotationAxis[1] * t;
    meshRef.current.rotation.z = rotationAxis[2] * t;
  });

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.8}>
      <mesh ref={meshRef} position={position}>
        {geometry}
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.12}
          wireframe
          side={THREE.DoubleSide}
        />
      </mesh>
    </Float>
  );
};

const Particles = ({ count = 60 }) => {
  const ref = useRef();

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 20;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 20;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return pos;
  }, [count]);

  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.y = state.clock.elapsedTime * 0.015;
    ref.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.01) * 0.1;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.03}
        color="#5eead4"
        transparent
        opacity={0.4}
        sizeAttenuation
      />
    </points>
  );
};

const shapes = [
  {
    position: [-5, 3, -4],
    geometry: <icosahedronGeometry args={[1.2, 0]} />,
    color: '#00d4aa',
    speed: 0.15,
    rotationAxis: [0.3, 1, 0.2],
  },
  {
    position: [5.5, -2, -3],
    geometry: <octahedronGeometry args={[0.9, 0]} />,
    color: '#f59e0b',
    speed: 0.2,
    rotationAxis: [0.5, 0.3, 1],
  },
  {
    position: [-3, -4, -5],
    geometry: <tetrahedronGeometry args={[0.8, 0]} />,
    color: '#0ea5e9',
    speed: 0.18,
    rotationAxis: [1, 0.5, 0.3],
  },
  {
    position: [4, 4.5, -6],
    geometry: <dodecahedronGeometry args={[0.7, 0]} />,
    color: '#5eead4',
    speed: 0.12,
    rotationAxis: [0.2, 0.8, 0.5],
  },
  {
    position: [-6, 0, -7],
    geometry: <torusGeometry args={[0.6, 0.2, 8, 16]} />,
    color: '#f59e0b',
    speed: 0.1,
    rotationAxis: [0.6, 0.4, 0.2],
  },
  {
    position: [2, -5, -4],
    geometry: <boxGeometry args={[0.8, 0.8, 0.8]} />,
    color: '#00d4aa',
    speed: 0.22,
    rotationAxis: [0.4, 0.6, 1],
  },
];

const SceneBg = () => {
  return (
    <div className="scene-bg">
      <Canvas
        camera={{ position: [0, 0, 6], fov: 60 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[5, 5, 5]} intensity={0.3} />
        {shapes.map((props, i) => (
          <Shape key={i} {...props} />
        ))}
        <Particles />
      </Canvas>
    </div>
  );
};

export default SceneBg;
